/**
 * Catálogo de moedas — fonte única de verdade.
 *
 * Este arquivo é DUPLICADO nos 4 projetos (backend-pedeja/lib, pedeja-front/src/utils,
 * garcomapp-front/src/utils, pedeja-print/lib). Ao mudar aqui, replicar nos outros três.
 * Não tem nenhuma dependência de propósito, justamente para poder ser copiado
 * sem arrastar nada junto.
 *
 * Descoberta que reduziu o escopo desta feature: o `Intl.NumberFormat` com
 * `style: 'currency'` JÁ conhece a escala de cada moeda — LBP sai como
 * "LBP 895,000" (zero casas) sem nenhuma configuração nossa. Quem quebrava era o
 * nosso `formatPrice`, que cravava `minimumFractionDigits: 2`, e os `toFixed(2)`
 * espalhados. Por isso `decimals` aqui serve para INPUT e ARREDONDAMENTO, não
 * para exibição — a exibição delega ao Intl.
 */

export const CURRENCIES = {
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Real',
    locale: 'pt-BR',
    decimals: 2,
    // Menor incremento com que se opera. Usado para arredondar conversões.
    step: 0.01,
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'Dólar',
    locale: 'en-US',
    decimals: 2,
    step: 0.01,
  },
  LBP: {
    code: 'LBP',
    // 'LBP' em ASCII de propósito: na comanda térmica o símbolo árabe (ل.ل.)
    // não existe em nenhuma code page suportada e sairia como '?'.
    // Alternativa viável se um dia quisermos: o francês '£LB' (a nota diz
    // "CENT MILLE LIVRES" / "BANQUE DU LIBAN"), e o '£' É imprimível em CP860
    // (0x9C) — verificado. Fica como opção, não como default.
    symbol: 'LBP',
    // ATENÇÃO: o código ISO significa "Lebanese Pound", e tanto o Intl quanto a
    // nota chamam de "libra"/"livre" — mas NINGUÉM no Líbano usa esses nomes.
    // Confirmado com falante nativo (Rawad, 07/2026): "Lira mesmo".
    // Não trocar por "Libra libanesa" achando que é mais correto: é o nome que
    // o dono do restaurante NÃO reconhece.
    name: 'Lira',
    locale: 'en-US',
    // A lira não tem subunidade em uso — preço é sempre inteiro.
    decimals: 0,
    step: 1,
    // NOTA: arredondamento COMERCIAL (arredondar a conta para o milhar mais
    // próximo, por exemplo) é outra coisa e ainda depende de decisão do PO.
    // `step: 1` aqui é só a granularidade da moeda.
  },
};

export const DEFAULT_CURRENCY = 'USD';

/** Metadados de uma moeda, com fallback seguro. Nunca lança. */
export function getCurrencyMeta(code) {
  if (code && CURRENCIES[code]) return CURRENCIES[code];
  return CURRENCIES[DEFAULT_CURRENCY];
}

/** A moeda é conhecida pelo catálogo? */
export function isSupportedCurrency(code) {
  return Boolean(code && CURRENCIES[code]);
}

/** Lista de códigos suportados (para selects e validação). */
export function listCurrencyCodes() {
  return Object.keys(CURRENCIES);
}

/**
 * Arredonda um valor para a granularidade da moeda.
 * Sem isso, converter USD->LBP produz "895000.0000001" e centavos de lira,
 * que não existem.
 */
export function roundToCurrency(value, code) {
  const { step } = getCurrencyMeta(code);
  const n = Number(value) || 0;
  if (step >= 1) return Math.round(n / step) * step;

  // Step fracionário (centavos). Multiplicar por 100 antes de arredondar NÃO
  // basta: em float, 8.745 * 100 vira 874.4999999999999, e Math.round devolve
  // 874 -> 8,74 em vez de 8,75. Numa gorjeta de 10% isso subcobra um centavo
  // em silêncio, e sempre para o mesmo lado.
  // `toPrecision(12)` normaliza o ruído binário antes do arredondamento —
  // 12 dígitos significativos cobrem qualquer valor monetário real com folga.
  const inverse = Math.round(1 / step);
  return Math.round(Number((n * inverse).toPrecision(12))) / inverse;
}
